import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { VideoInspection, VideoPoster } from '../lib/video';
import { captureVideoPoster, defaultPosterTimestamp } from '../lib/video';
import { formatDuration } from './VideoGallery';
import { Button } from './ui/Button';

export function VideoPosterChooser({
  file,
  inspection,
  signal,
  onCancel,
  onConfirm
}: {
  file: File;
  inspection: VideoInspection;
  signal?: AbortSignal;
  onCancel: () => void;
  onConfirm: (poster: VideoPoster) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [timestamp, setTimestamp] = useState(() => defaultPosterTimestamp(inspection.durationSeconds));
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState('');

  useEffect(() => {
    const nextUrl = URL.createObjectURL(file);
    setUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);

  async function handleConfirm() {
    if (!videoRef.current) {
      return;
    }
    setCapturing(true);
    setError(null);
    try {
      onConfirm(await captureVideoPoster(videoRef.current, timestamp, signal));
    } catch (captureError) {
      if (captureError instanceof DOMException && captureError.name === 'AbortError') {
        return;
      }
      setError(captureError instanceof Error ? captureError.message : 'Could not create the poster frame.');
    } finally {
      setCapturing(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/55 p-4" role="dialog" aria-modal="true" aria-label="Choose video poster">
      <div className="w-full max-w-3xl rounded-sm bg-bone p-5 shadow-soft">
        <div>
          <h2 className="text-lg font-semibold text-ink">Choose a poster frame</h2>
          <p className="mt-1 truncate text-sm text-ink/70">{file.name}</p>
        </div>
        <video
          ref={videoRef}
          src={url || undefined}
          muted
          playsInline
          preload="auto"
          onLoadedData={(event) => { event.currentTarget.currentTime = timestamp; }}
          className="mt-5 aspect-video w-full rounded-sm bg-black object-contain"
        />
        <label className="mt-5 grid gap-2 text-sm font-medium text-ink">
          <span className="flex justify-between gap-3"><span>Poster time</span><span>{formatDuration(timestamp)}</span></span>
          <input
            type="range"
            min={0}
            max={inspection.durationSeconds}
            step={0.1}
            value={timestamp}
            onChange={(event) => {
              const next = Number(event.target.value);
              setTimestamp(next);
              if (videoRef.current) {
                videoRef.current.currentTime = next;
              }
            }}
            className="accent-sea"
          />
        </label>
        {error ? <p className="mt-3 text-sm text-clay">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={capturing}>Cancel</Button>
          <Button type="button" onClick={() => void handleConfirm()} disabled={capturing || !url}>
            {capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            <span>{capturing ? 'Creating poster' : 'Upload video'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
